import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { parseISO, formatISO, format, isValid } from 'date-fns';
import { useAtom } from 'jotai';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Checkbox, DatePicker, Heading, TextInput } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

import useUpdateJobApplication from '../../../hooks/use-update-job-application';
import { candidateModalVariantAtom, isCandidateModalSuccessScreenAtom } from '../../../employer-job-listing.atoms';
import { useEmployerJobApplicationQueryKey } from '../../../hooks/use-employer-job-applications';

export enum InterviewScheduleSteps {
  ACTION_CONFIRMATION,
  SELECT_DATE_TIME,
  VIEW_SUCCESS_MESSAGE,
}

function CandidateInterviewDateTime({
  firstName,
  jobPositionId,
  workerId,
  setStep,
  step,
  isEdit,
  interviewDate,
  onRemindersChange,
}: {
  firstName: string;
  workerId: string;
  jobPositionId: string;
  setStep: Dispatch<SetStateAction<InterviewScheduleSteps>>;
  step: InterviewScheduleSteps;
  isEdit?: boolean;
  interviewDate?: string | null;
  onRemindersChange?: (withReminders: boolean) => void;
}) {
  const { t: translateCommon } = useTranslation('common');
  const { t } = useTranslation('job-positions-page');

  const { control, handleSubmit, watch } = useForm<InterviewFormValues>({
    defaultValues: getInterviewDefaults(interviewDate),
  });
  const { mutate, isSuccess, isPending } = useUpdateJobApplication();
  const queryClient = useQueryClient();
  const [, setCandidateModalVariant] = useAtom(candidateModalVariantAtom);
  const [, setIsCandidateModalSuccessScreen] = useAtom(isCandidateModalSuccessScreenAtom);

  // The tool only sets reminders for an interview the employer has already agreed with the
  // candidate: Restworld does not contact the candidate, so the confirmation is required.
  // It starts unchecked on edit too.
  const [agreed, setAgreed] = useState(false);

  const canSubmit = !!watch('date') && !!watch('time') && agreed;

  const onSubmit: SubmitHandler<InterviewFormValues> = useCallback(
    (values) => {
      const interviewDate = values.date && values.time ? toISODateTime(values.date, values.time) : null;
      if (interviewDate) onRemindersChange?.(true);
      mutate({
        params: {
          interview_date: interviewDate,
          // On edit the candidate is already in `worker_jp_interview_scheduled`, so we
          // update the datetime only. Re-sending the same status_key would hit the state
          // machine's same-state guard and be rejected; omitting it takes the direct-update
          // path, which still fires the "interview rescheduled" email + reminder refresh.
          ...(isEdit ? {} : { status_key: 'worker_jp_interview_scheduled' }),
        },
        workerId,
        jobPositionId,
      });
    },
    [isEdit, jobPositionId, mutate, onRemindersChange, workerId]
  );

  // Moves the candidate without a date, whatever is typed in the form: sending the date
  // would schedule the interview and email the candidate.
  const onSkip = useCallback(() => {
    onRemindersChange?.(false);
    mutate({
      params: { interview_date: null, status_key: 'worker_jp_interview_scheduled' },
      workerId,
      jobPositionId,
    });
  }, [jobPositionId, mutate, onRemindersChange, workerId]);

  const handleCancel = useCallback(() => {
    // In edit mode we open straight on the date/time step, so cancelling just
    // returns to the default candidate view instead of stepping back.
    if (isEdit) {
      setCandidateModalVariant('default');
      return;
    }
    setStep((prev) => (prev > 0 ? prev - 1 : prev));
    setCandidateModalVariant('default');
  }, [isEdit, setCandidateModalVariant, setStep]);

  const employerJobApplicationQueryKey = useEmployerJobApplicationQueryKey();

  useEffect(() => {
    if (!isSuccess) return;
    queryClient.invalidateQueries({ queryKey: employerJobApplicationQueryKey });
    setIsCandidateModalSuccessScreen(true);
    setStep(InterviewScheduleSteps.VIEW_SUCCESS_MESSAGE);
  }, [employerJobApplicationQueryKey, isSuccess, queryClient, setIsCandidateModalSuccessScreen, setStep]);

  return (
    <StyledForm>
      <ContentAndInputsWrapper>
        <CalendarDaysIcon className="h-6 w-6 text-neutral-700 mx-auto mb-5" />
        <Heading color="neutral" className="w-full text-base lg:text-lg">
          {isEdit
            ? t('edit_interview_datetime')
            : t('have_you_already_scheduled_the_interview_with', { name: firstName })}
        </Heading>
        <ContentWrapper>{t('interview_reminders_explanation', { name: firstName })}</ContentWrapper>
        <InputsWrapper>
          <InputWrapper>
            <StyledLabel htmlFor="date">{t('select_the_date_of_the_interview')}</StyledLabel>
            <Controller
              name="date"
              control={control}
              render={({ field }) => <DatePicker borderColor="neutral-100" {...field} />}
            />
          </InputWrapper>
          <InputWrapper>
            <StyledLabel htmlFor="time">{t('select_the_time_of_the_interview')}</StyledLabel>
            <Controller
              name="time"
              control={control}
              render={({ field }) => (
                // Native time input on all breakpoints. The desktop-only rc-time-picker crashed on
                // React 19 (it calls the removed ReactDOM.findDOMNode); this matches the mobile field
                // and works with the same `HH:mm` string value the form already stores.
                <TextInput {...field} id="time" type="time" placeholder="HH:MM" className="w-full" />
              )}
            />
          </InputWrapper>
          <Checkbox
            id="interview-agreed"
            title={
              <label
                htmlFor="interview-agreed"
                className="text-start text-neutral-700 text-base font-medium cursor-pointer"
              >
                {t('interview_agreed_with_candidate', { name: firstName })}
              </label>
            }
            isChecked={agreed}
            onChange={() => setAgreed(!agreed)}
          />
        </InputsWrapper>
      </ContentAndInputsWrapper>
      <ButtonsWrapper>
        <ButtonV2 type="button" variant="tertiary" className="w-full lg:w-auto" onClick={handleCancel}>
          {translateCommon('cancel')}
        </ButtonV2>
        <EndActionsWrapper>
          {!isEdit && (
            <ButtonV2
              type="button"
              variant="secondary-neutral"
              loading={isPending}
              className="bg-transparent w-full lg:w-auto"
              onClick={onSkip}
            >
              {t('not_yet_will_contact_first')}
            </ButtonV2>
          )}
          <ButtonV2
            className="w-full lg:w-auto"
            loading={isPending}
            disabled={!canSubmit}
            onClick={handleSubmit(onSubmit)}
          >
            {isEdit ? translateCommon('save') : t('activate_reminders')}
          </ButtonV2>
        </EndActionsWrapper>
      </ButtonsWrapper>
    </StyledForm>
  );
}
export default CandidateInterviewDateTime;

const StyledForm = ({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form
    className={cn('h-full flex flex-col justify-between lg:min-h-full lg:h-auto lg:px-10 lg:pb-10 lg:pt-4', className)}
    {...props}
  />
);
const ContentWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-neutral-200 mt-5 mb-12 font-medium', className)} {...props} />
);
// Cap the scrollable content to the viewport (minus the fixed header + footer) so the
// modal panel's `min-h-fit` can't grow past the screen. Keeps the content scrollable at
// any zoom while the header stays on top and the footer stays visible at the bottom.
// `-mx-20 px-10` full-bleeds the scroll area past the two ancestor padding layers
// (MainContentWrapper px-10 + StyledForm px-10) so the scrollbar sits at the modal edge,
// then re-insets the content with its own gutter.
const ContentAndInputsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:max-h-[calc(95vh-220px)] lg:-mx-20 lg:px-10', className)}
    {...props}
  />
);
const InputsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('max-w-[320px] mx-auto flex flex-col gap-10', className)} {...props} />
);
const InputWrapper = ({
  isHidden,
  className,
  ...props
}: { isHidden?: boolean } & React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2', isHidden && 'hidden', className)} {...props} />
);
const StyledLabel = ({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('text-start font-medium text-neutral-700 text-base', className)} {...props} />
);
const ButtonsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      // Below lg the three actions stack full width (primary on top): side by side, the longer
      // labels of the reminders-only step wrapped on four lines and were cut off on mobile.
      'mt-8 flex flex-col-reverse gap-3 p-4 border-neutral-40 lg:flex-row lg:justify-between lg:items-center lg:gap-4 lg:px-20 lg:border-t lg:shrink-0 lg:-mx-10 lg:-mb-10',
      className
    )}
    {...props}
  />
);
const EndActionsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex flex-col-reverse gap-3 w-full lg:w-auto lg:flex-row lg:items-center lg:gap-5', className)}
    {...props}
  />
);

interface InterviewFormValues {
  date?: string;
  time?: string;
}

function toISODateTime(date: string, time: string): string {
  const dateTimeString = `${date}T${time}:00`;
  const parsedDate = parseISO(dateTimeString);
  return formatISO(parsedDate);
}

// Splits a stored ISO interview datetime into the `date` (yyyy-MM-dd) and `time`
// (HH:mm) shape the form fields expect, so the pickers open prefilled when editing.
function getInterviewDefaults(interviewDate?: string | null): InterviewFormValues {
  if (!interviewDate) return {};
  const parsed = parseISO(interviewDate);
  if (!isValid(parsed)) return {};
  return {
    date: format(parsed, 'yyyy-MM-dd'),
    time: format(parsed, 'HH:mm'),
  };
}
